import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cleanParams } from '../utils/cleanParams';

interface IUseCustomSearchProps {
  initType?: string;
  searchKey?: string;
  pageKey?: string;
  searchWordKey?: string;
}

export const useCustomSearch = ({
  initType,
  searchKey = 'searchType',
  pageKey = 'page',
  searchWordKey = 'keyword',
}: IUseCustomSearchProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams('');
  const searchTypeUrl = searchParams.get(searchKey) || initType;
  const searchWordUrl = searchParams.get(searchWordKey) || '';

  const handleSearch = async (
    e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement>,
    selectedSearchType = searchTypeUrl,
  ) => {
    e.preventDefault();

    if (e.type === 'keyup' && (e as React.KeyboardEvent).key !== 'Enter') {
      return;
    }

    if (inputRef.current) {
      const currentParams = Object.fromEntries(searchParams.entries());

      setSearchParams(
        cleanParams({
          ...currentParams,
          [pageKey]: '1',
          [searchKey]: selectedSearchType || '',
          [searchWordKey]: inputRef.current.value || '',
        }),
      );
    }
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const resetWord = (selectedSearchType = searchTypeUrl) => {
    if (!inputRef.current) return;

    inputRef.current.value = '';
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams(
      cleanParams({
        ...currentParams,
        [pageKey]: '1',
        [searchKey]: selectedSearchType || '',
        [searchWordKey]: '',
      }),
    );
  };

  const handleSearchType = (value: string | null) => {
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams(
      cleanParams({
        ...currentParams,
        [pageKey]: '1',
        [searchKey]: value || '',
      }),
    );
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = searchWordUrl;
    }
  }, [searchWordUrl]);

  return {
    inputRef,
    handleSearch,
    resetWord,
    resetSearchInput,
    handleSearchType,
  };
};
